/** Isometric diamond tile size in world pixels. */
export const TILE_W = 32;
export const TILE_H = 16;

export interface IsoOrigin {
  readonly ox: number;
  readonly oy: number;
}

/** Grid coordinates (tiles, fractional) → world pixel coordinates. */
export function toScreen(gx: number, gy: number, o: IsoOrigin): [number, number] {
  return [(gx - gy) * (TILE_W / 2) + o.ox, (gx + gy) * (TILE_H / 2) + o.oy];
}

/** World pixel coordinates → grid coordinates. Inverse of {@link toScreen}. */
export function toWorld(sx: number, sy: number, o: IsoOrigin): [number, number] {
  const a = (sx - o.ox) / (TILE_W / 2);
  const b = (sy - o.oy) / (TILE_H / 2);
  return [(a + b) / 2, (b - a) / 2];
}

/**
 * Converts a screen-space direction (e.g. WASD) to a normalized grid direction,
 * so "up" on screen walks toward the top of the diamond.
 */
export function screenDirToWorld(sx: number, sy: number): [number, number] {
  if (!sx && !sy) return [0, 0];
  const wx = (sx / (TILE_W / 2) + sy / (TILE_H / 2)) / 2;
  const wy = (sy / (TILE_H / 2) - sx / (TILE_W / 2)) / 2;
  const l = Math.hypot(wx, wy);
  return [wx / l, wy / l];
}

/** Sign of the on-screen horizontal component of a grid delta. Used for sprite facing. */
export const screenFacing = (dx: number, dy: number): number => dx - dy;
/**
 * Screen octant of a grid direction: 0 = screen-right, then clockwise in 45° steps
 * (1 down-right, 2 down, 3 down-left, 4 left, 5 up-left, 6 up, 7 up-right).
 */
export function screenHeading(dx: number, dy: number): number {
  const a = Math.atan2((dx + dy) * (TILE_H / TILE_W), dx - dy);
  return (((Math.round(a / (Math.PI / 4)) % 8) + 8) % 8) as number;
}
